/**
 * useSearchReferrerTracking
 *
 * Detecta cómo nos están buscando y persiste el insight en PostHog para
 * que el admin pueda ver:
 *   - Qué motores de búsqueda envían tráfico (Google, Bing, DuckDuckGo)
 *   - Qué query usaron (cuando está disponible — la mayoría de browsers
 *     strippean el query string del referrer por privacy, pero Bing y
 *     algunos casos legacy aún lo dejan)
 *   - Qué landing path matchea con la query (señal de SEO performance)
 *   - SiteLinks Search Box: cuando Google muestra el search box dentro
 *     del SERP (habilitado por WebSite.potentialAction.SearchAction en
 *     JSON-LD), el visitor llega con `?q=user_query` — eso SÍ es leíble.
 *
 * Estrategia:
 *   - One-shot al primer mount (no en cada nav SPA — solo el referrer
 *     externo cuenta).
 *   - Dispara `search_referral_landed` a PostHog. El admin puede armar
 *     dashboards en PostHog directamente filtrando por ese event.
 *   - Cookie session sentinel para no re-disparar dentro de la misma
 *     visita si el user navega entre páginas internas.
 *
 * Privacy: respetamos el consent de cookies. Si PostHog no está cargado
 * o el user denegó consent, el hook es no-op silencioso.
 */

import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { detectTrafficSource } from "@/lib/detectTrafficSource";

const SESSION_SENTINEL_KEY = "search_referral_tracked";

interface SearchReferralPayload {
  search_engine: string; // "google" | "bing" | "duckduckgo" | ...
  search_query: string | null; // null cuando el browser lo strippeó
  landing_path: string; // path donde aterrizó
  landing_url: string; // URL completa
  referrer: string; // referrer raw
  source: string; // source detectado por detectTrafficSource
  medium: string; // medium (organic, cpc, social, etc.)
  is_sitelinks_search: boolean; // true cuando viene de SiteLinks Search Box
}

/**
 * Devuelve el query si lo encuentra en la URL o en el referrer.
 * Prioridad: ?q= en URL local (SiteLinks Search Box) → q en referrer.
 */
function extractSearchQuery(referrer: string): string | null {
  // 1. ?q= en URL local (SiteLinks Search Box de Google envía al user con
  //    /?q=user_search). Eso lo entendemos como query directa del visitor.
  if (typeof window !== "undefined") {
    const localParams = new URLSearchParams(window.location.search);
    const q = localParams.get("q") || localParams.get("query");
    if (q && q.trim()) return q.trim().slice(0, 200);
  }
  // 2. Referrer puede tener el query (legacy / Bing / mobile sin strip).
  if (!referrer) return null;
  try {
    const url = new URL(referrer);
    // Google: q | Bing: q | Yahoo: p | DuckDuckGo: q | Yandex: text
    const params = url.searchParams;
    for (const key of ["q", "p", "text", "search", "query"]) {
      const v = params.get(key);
      if (v && v.trim()) return v.trim().slice(0, 200);
    }
  } catch {
    return null;
  }
  return null;
}

/** True cuando el visitor entró via `?q=` (SiteLinks Search Box). */
function isSiteLinksSearchEntry(): boolean {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  return params.has("q") || params.has("query");
}

function isSearchEngineSource(source: string): boolean {
  return ["google", "bing", "yahoo", "duckduckgo", "yandex", "baidu", "ecosia"].includes(
    source,
  );
}

export function useSearchReferrerTracking() {
  const location = useLocation();
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return;
    if (typeof window === "undefined") return;
    // Session sentinel evita re-disparar en cada SPA-nav. Se resetea al
    // cerrar la tab (sessionStorage).
    try {
      if (sessionStorage.getItem(SESSION_SENTINEL_KEY) === "1") {
        firedRef.current = true;
        return;
      }
    } catch {
      // sessionStorage puede tirar en private mode — seguimos.
    }

    const traffic = detectTrafficSource();
    const referrer = (typeof document !== "undefined" && document.referrer) || "";
    const isSearch = isSearchEngineSource(traffic.source);
    const isSiteLinks = isSiteLinksSearchEntry();

    // Solo trackeamos cuando hay señal de search (referrer de search engine,
    // o ?q= local que es SiteLinks). UTM-only o direct no contamos.
    if (!isSearch && !isSiteLinks) {
      firedRef.current = true;
      return;
    }

    const query = extractSearchQuery(referrer);

    const payload: SearchReferralPayload = {
      search_engine: isSiteLinks && !isSearch ? "google" : traffic.source,
      search_query: query,
      landing_path: location.pathname,
      landing_url: window.location.href,
      referrer,
      source: traffic.source,
      medium: traffic.medium,
      is_sitelinks_search: isSiteLinks,
    };

    // PostHog: captura el evento. El admin puede armar funnels/dashboards
    // filtrando por `search_referral_landed` y agrupando por landing_path
    // o search_query.
    try {
      const ph = (window as unknown as { posthog?: { capture?: (name: string, props: unknown) => void } }).posthog;
      if (ph?.capture) {
        ph.capture("search_referral_landed", payload);
      }
    } catch {
      // best-effort, no rompemos la página por un track
    }

    // Persistir en DB para admin (vista agregada). Best-effort, no bloquea.
    try {
      void fetch("/api/search-referrals", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        keepalive: true,
      });
    } catch {
      /* swallow */
    }

    try {
      sessionStorage.setItem(SESSION_SENTINEL_KEY, "1");
    } catch {
      /* ignore */
    }
    firedRef.current = true;
  }, [location.pathname]);
}
